import React, { useState, useEffect, useRef, useCallback } from 'react';
import { dashboardAPI, leadsAPI } from '../services/api';
import FilterBar from './FilterBar';
import { Trash2, CheckCircle, Undo2, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Pipeline Clients — between Potential and Onboarded.
 *
 * A potential client is someone we are still following. A PIPELINE client has
 * said they want to sign; the deal is real but parked (waiting on legal, budget,
 * a signature). So the columns that matter here are the ones that explain the
 * wait: what it is on hold for, when we expect it to close, and where the
 * contract stands.
 */
const CONTRACT_STATUSES = ['In discussion', 'Proposal sent', 'Contract sent', 'On hold', 'Verbal yes', 'Legal review'];

const PipelineClients = ({ onNavigateToPreSales, onExportExcel, onExportPDF, showExport = false }) => {
  const { isAdmin, user } = useAuth();

  const canDelete = () => {
    if (isAdmin()) return true;
    const userName = (user?.name || '').toLowerCase().trim();
    return userName.includes('hima') || userName.includes('shree');
  };

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBDE, setSelectedBDE] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [edits, setEdits] = useState({}); // id -> pending field changes
  const [savingId, setSavingId] = useState(null);
  const hasLoadedRef = useRef(false);

  const normalizeUrl = (url) => {
    if (!url) return '';
    const trimmed = url.trim();
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };

  const fetchClients = useCallback(async () => {
    try {
      if (!hasLoadedRef.current) setLoading(true);
      const result = await dashboardAPI.getPipelineClients({
        bde: selectedBDE !== 'All' ? selectedBDE : undefined,
        search: searchTerm || undefined
      });
      if (result.success) setClients(result.data || []);
    } catch (error) {
      console.error('Error fetching pipeline clients:', error);
    } finally {
      setLoading(false);
      hasLoadedRef.current = true;
    }
  }, [selectedBDE, searchTerm]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // Let other tabs refresh this list after they push a company into pipeline.
  useEffect(() => {
    window.pipelineClientsRefresh = fetchClients;
    return () => { delete window.pipelineClientsRefresh; };
  }, [fetchClients]);

  // Converted-date range is filtered here; the endpoint filters by BDE + search.
  const visible = clients.filter(c => {
    if (dateFrom && (!c.converted_date || c.converted_date < dateFrom)) return false;
    if (dateTo && (!c.converted_date || c.converted_date > dateTo)) return false;
    return true;
  });

  const fieldValue = (client, field) => (
    edits[client.id]?.[field] !== undefined ? edits[client.id][field] : (client[field] ?? '')
  );

  const setField = (id, field, value) => {
    setEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const saveRow = async (client) => {
    const pending = edits[client.id];
    if (!pending) return;
    try {
      setSavingId(client.id);
      const result = await dashboardAPI.updatePipelineClient(client.id, pending);
      if (result.success) {
        setClients(prev => prev.map(c => (c.id === client.id ? { ...c, ...result.data } : c)));
        setEdits(prev => { const next = { ...prev }; delete next[client.id]; return next; });
      } else {
        alert('Could not save: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Could not save: ' + (error.response?.data?.error || error.message));
    } finally {
      setSavingId(null);
    }
  };

  const onboard = async (client) => {
    if (!window.confirm(`"${client.company_name}" signed the contract? This moves them to Onboarded Clients.`)) return;
    try {
      setLoading(true);
      const result = await dashboardAPI.convertPipelineToOnboarded(client.id, undefined, user?.id);
      if (result.success) {
        fetchClients();
        if (window.onboardedClientsRefresh) window.onboardedClientsRefresh();
      } else {
        alert('Error: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error onboarding: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const backToPotential = async (client) => {
    if (!window.confirm(`Move "${client.company_name}" back to Potential Clients?`)) return;
    try {
      setLoading(true);
      const result = await dashboardAPI.movePipelineBackToPotential(client.id);
      if (result.success) {
        fetchClients();
        if (window.potentialClientsRefresh) window.potentialClientsRefresh();
      } else {
        alert('Error: ' + (result.error || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const remove = async (client) => {
    if (!window.confirm(`Delete "${client.company_name}" from Pipeline Clients?`)) return;
    try {
      setLoading(true);
      const result = await dashboardAPI.deletePipelineClient(client.id);
      if (result.success) fetchClients();
      else alert('Error deleting: ' + (result.error || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const openLead = async (client, e) => {
    if (!onNavigateToPreSales || !client.source_lead_id) return;
    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('input') || e.target.closest('select')) return;
    try {
      const r = await leadsAPI.getById(client.source_lead_id);
      if (r.data) onNavigateToPreSales(r.data);
    } catch (err) { console.error('Error fetching lead:', err); }
  };

  return (
    <div className="space-y-4">
      <FilterBar
        selectedDate="" setSelectedDate={() => {}}
        dateFrom={dateFrom} setDateFrom={setDateFrom}
        dateTo={dateTo} setDateTo={setDateTo}
        selectedBDE={selectedBDE} setSelectedBDE={setSelectedBDE}
        selectedStage="All" setSelectedStage={() => {}}
        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
        showDateRange={true} showSearch={true} showStageFilter={false}
        onExportExcel={onExportExcel} onExportPDF={onExportPDF} showExport={showExport}
      />

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Pipeline Clients ({visible.length})</h2>
        <p className="text-xs text-gray-500">They want to sign — waiting on contract, budget or sign-off</p>
      </div>

      {loading ? (
        <div className="text-center py-10">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#679632]" />
          <p className="mt-2 text-gray-500 text-sm">Loading...</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-400 text-sm">No pipeline clients yet</p>
          <p className="text-gray-400 text-xs mt-1">Move a company here from Potential Clients when they say they want to sign.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">Since</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">Company</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">POC</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">Contract</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">On hold for</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">Expected close</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase">BDA</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map((client) => {
                  const dirty = !!edits[client.id];
                  return (
                    <tr key={client.id} className="hover:bg-gray-50 cursor-pointer" onClick={(e) => openLead(client, e)}>
                      <td className="px-4 py-2.5 text-sm text-gray-600 whitespace-nowrap">
                        {client.converted_date
                          ? (() => { const [y, m, d] = client.converted_date.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }); })()
                          : '-'}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-[#679632] hover:underline">{client.company_name}</span>
                          {client.company_url && (
                            <a href={normalizeUrl(client.company_url)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-gray-400 hover:text-blue-600" title="Website">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                            </a>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{[client.city, client.country].filter(Boolean).join(', ')}</p>
                      </td>
                      <td className="px-4 py-2.5">
                        <p className="text-sm text-gray-900">{client.poc_name || '-'}</p>
                        {client.poc_email && <p className="text-xs text-gray-500 truncate max-w-[180px]">{client.poc_email}</p>}
                      </td>
                      <td className="px-4 py-2.5">
                        <select
                          value={fieldValue(client, 'contract_status') || 'In discussion'}
                          onChange={(e) => setField(client.id, 'contract_status', e.target.value)}
                          onClick={e => e.stopPropagation()}
                          className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                        >
                          {CONTRACT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2.5">
                        <input
                          type="text"
                          value={fieldValue(client, 'hold_reason')}
                          onChange={(e) => setField(client.id, 'hold_reason', e.target.value)}
                          onClick={e => e.stopPropagation()}
                          placeholder="e.g. legal review"
                          className="border border-gray-300 rounded px-2 py-1 text-xs w-40"
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <input
                          type="date"
                          value={fieldValue(client, 'expected_close_date') || ''}
                          onChange={(e) => setField(client.id, 'expected_close_date', e.target.value)}
                          onClick={e => e.stopPropagation()}
                          className="border border-gray-300 rounded px-2 py-1 text-xs"
                        />
                      </td>
                      <td className="px-4 py-2.5 text-sm text-gray-600">{client.bde_name || '-'}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1.5">
                          {dirty && (
                            <button
                              onClick={(e) => { e.stopPropagation(); saveRow(client); }}
                              disabled={savingId === client.id}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
                            >
                              <Save size={13} /> {savingId === client.id ? 'Saving…' : 'Save'}
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); onboard(client); }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#679632] text-white rounded text-xs font-medium hover:bg-[#587f2b]"
                            title="Contract signed — move to Onboarded Clients"
                          >
                            <CheckCircle size={13} /> Onboard
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); backToPotential(client); }}
                            className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
                            title="Move back to Potential Clients"
                          >
                            <Undo2 size={14} />
                          </button>
                          {canDelete() && (
                            <button
                              onClick={(e) => { e.stopPropagation(); remove(client); }}
                              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineClients;
